import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { InteractivesListQuery, InteractivesListResponse } from "../models/interactives-list.model";

// Largest `size` the backend accepts
const MAX_PAGE_SIZE = 200;

@Injectable({ providedIn: "root" })
export class InteractivesApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.interactiveExperienceServiceUrl}/v1/interactives`;

    /**
     * Fetches the interactives list, optionally filtered by status, type and tenant. Empty filters
     * are omitted from the request so the backend applies its "all" defaults (e.g. no `statusFilter`
     * returns all non-archived interactives); the backend omits archived interactives unless
     * `includeArchived` is explicitly set to true.
     */
    getList(query: InteractivesListQuery = {}): Observable<InteractivesListResponse> {
        let params = new HttpParams();
        if (query.includeStats) {
            params = params.set("includeStats", query.includeStats);
        }
        if (query.status) {
            params = params.set("statusFilter", query.status);
        }
        if (query.type) {
            params = params.set("type", query.type);
        }
        if (query.tenantId) {
            params = params.set("tenantIds", query.tenantId);
        }
        if (query.page !== undefined) {
            params = params.set("page", query.page + 1);
        }
        if (query.size !== undefined) {
            params = params.set("size", query.size);
        }
        if (query.includeArchived) {
            params = params.set("includeArchived", true);
        }
        return this.http.get<InteractivesListResponse>(this.baseUrl, { params });
    }

    /**
     * Wraps `getList` for callers that want every interactive for a tenant (e.g. a dropdown),
     * not a paginated page. Not a true "getAll" — it's just `getList` at the largest size the backend accept
     *
     * Leaves `includeStats` false since callers of this wrapper only need the list itself;
     * passing true would compute stats for every returned interactive, which costs more the larger `size` is.
     */
    getAllInteractives(tenantId: string, includeArchived = false): Observable<InteractivesListResponse> {
        return this.getList({ tenantId, includeArchived, size: MAX_PAGE_SIZE });
    }
}
